/**
 * Editorial content seed — ONE source of truth for the MB Group service
 * taxonomy, shared by two consumers that must never drift apart:
 *
 *   1. apps/frontend  — builds its offline DEMO dataset from it, so the site
 *                       stays complete when the GraphQL gateway is unreachable.
 *   2. apps/strapi    — its `seed` script reads the same JSON files (by path,
 *                       because Strapi installs outside the pnpm workspace) and
 *                       upserts them into the CMS as the LIVE content.
 *
 * The taxonomy itself comes from the approved "MB Group 6" mock-up: eight
 * service categories over 38 services, plus industries, case studies, insight
 * articles, group pillars and lead magnets. PL is the source of truth; EN is a
 * translation with the same ids and structure (only `slug` differs per locale).
 */
import type { Locale } from "@mb-group/shared-types";
import plJson from "./content.pl.json";
import enJson from "./content.en.json";

/** A single service — the leaf of the taxonomy. */
export interface ServiceSeed {
	/** Locale-independent identity; the same service has the same id in every locale. */
	id: string;
	/** Locale-specific URL slug. */
	slug: string;
	name: string;
	/** One-line teaser used on cards. */
	short: string;
	/** Full lead paragraph used on the detail page. */
	desc: string;
	/** Headline figure, e.g. "96%". */
	stat: string;
	statLabel: string;
	/** "What exactly we deliver" bullets. */
	spec: string[];
	/** "What we accept and handle" chips (material/product groups). */
	groups: string[];
}

export interface CategorySeed {
	id: string;
	/** Display ordinal from the mock-up, e.g. "01". */
	num: string;
	slug: string;
	title: string;
	summary: string;
	/** Ids into {@link ServiceSeed}, in display order. */
	serviceIds: string[];
}

export interface IndustrySeed {
	name: string;
	desc: string;
	tag: string;
}

export interface CaseSeed {
	slug: string;
	tag: string;
	title: string;
	challenge: string;
	solution: string;
	outcome: string;
	stat: string;
	statLabel: string;
	/** Basename of a file in the app's /static directory (no extension). */
	photo: string;
	serviceIds: string[];
}

export interface InsightSeed {
	slug: string;
	tag: string;
	title: string;
	excerpt: string;
	readingMinutes: number;
}

export interface PillarSeed {
	title: string;
	desc: string;
}

export interface MagnetSeed {
	slug: string;
	kind: string;
	tag: string;
	title: string;
	desc: string;
	meta: string;
	cta: string;
	doneTitle: string;
	doneDesc: string;
}

export interface ContentSeed {
	locale: Locale;
	services: ServiceSeed[];
	categories: CategorySeed[];
	industries: IndustrySeed[];
	cases: CaseSeed[];
	insights: InsightSeed[];
	pillars: PillarSeed[];
	magnets: MagnetSeed[];
}

// The JSON modules widen `locale` to `string`, so the cast goes through unknown.
// Shape conformance is asserted by content-seed.test.ts instead of the compiler.
const SEEDS: Record<Locale, ContentSeed> = {
	pl: plJson as unknown as ContentSeed,
	en: enJson as unknown as ContentSeed,
};

/** The full content seed for a locale (falls back to PL, the source of truth). */
export function contentSeed(locale: Locale): ContentSeed {
	return SEEDS[locale] ?? SEEDS.pl;
}

/** Services of a category, in the category's declared order. Unknown ids are skipped. */
export function categoryServices(seed: ContentSeed, categoryId: string): ServiceSeed[] {
	const category = seed.categories.find((c) => c.id === categoryId);
	if (!category) return [];
	const byId = new Map(seed.services.map((s) => [s.id, s]));
	return category.serviceIds
		.map((id) => byId.get(id))
		.filter((s): s is ServiceSeed => s !== undefined);
}

/** The category a service belongs to, or null when the service is unassigned. */
export function serviceCategory(seed: ContentSeed, serviceId: string): CategorySeed | null {
	return seed.categories.find((c) => c.serviceIds.includes(serviceId)) ?? null;
}

/** Every service paired with its owning category, flattened in category order. */
export function flatServices(
	seed: ContentSeed,
): { service: ServiceSeed; category: CategorySeed }[] {
	const byId = new Map(seed.services.map((s) => [s.id, s]));
	const out: { service: ServiceSeed; category: CategorySeed }[] = [];
	for (const category of seed.categories) {
		for (const id of category.serviceIds) {
			const service = byId.get(id);
			if (service) out.push({ service, category });
		}
	}
	return out;
}
